import React, { useState } from "react"
import styled from "styled-components"
import { Button, TextInput } from "@tmu/components/common"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTimes, faSparkles } from "@fortawesome/pro-regular-svg-icons"

// ... imports
import { useIntl } from "gatsby-plugin-intl"

const StudioContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 380px;
  height: auto;
  position: relative;
  font-family: 'Inter', sans-serif;
  background-color: #fff;
  width: 500px;
  max-width: 100%;

  @media (max-width: 600px) {
    width: 95vw;
    min-height: 50vh;
  }
`

const HeaderSection = styled.div`
  margin-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 10px;
`

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const Branding = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const LogoSphere = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 35%, #00d2ff 0%, #3a7bd5 100%);
  box-shadow: 0 4px 10px rgba(58, 123, 213, 0.3);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 6px;
    left: 10px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 70%);
  }
`

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
  letter-spacing: -0.5px;
`

const ControlsRow = styled.div`
    display: flex;
    gap: 12px;
    align-items: center;
`

const SelectInput = styled.select`
  width: 100%;
  padding: 14px 20px;
  padding-right: 48px; /* Space for custom arrow */
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background-color: #ffffff;
  font-size: 1rem;
  font-weight: 500;
  color: #1f2937;
  cursor: pointer;
  outline: none;
  transition: all 0.2s ease;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  
  /* Custom Arrow */
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 16px center;
  background-size: 20px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);

  &:hover {
    border-color: #cbd5e1;
    transform: translateY(-1px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.025);
  }
  
  &:focus {
      border-color: #00d2ff;
      box-shadow: 0 0 0 3px rgba(0, 210, 255, 0.15);
      transform: translateY(0);
  }
`

const FormContainer = styled.div`
    flex: 1;
    overflow-y: auto;
    padding-right: 10px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
`

const HalfWidthRow = styled.div`
    display: flex;
    gap: 20px;
    
    > div {
        flex: 1;
    }
`

const FooterActions = styled.div`
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid #eee;
    display: flex;
    align-items: center;
    justify-content: flex-end;
`

const IconButton = styled.button`
  background: transparent;
  color: #aaa;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f5f5f5;
    color: #666;
  }
`

const GenerateButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0.25rem 2rem;
  min-height: 2.5rem;
  border: none;
  border-radius: 0.25rem;
  background-color: rgba(10, 157, 189, 1);
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    background-color: rgba(10, 137, 169, 1);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const TrustAIStudio = ({ onGenerate, onClose }) => {
  const { formatMessage } = useIntl()

  // Load data and step from localStorage (cache)
  const [step, setStep] = useState(() => {
    const cachedStep = localStorage.getItem("trustai_wizard_step")
    return cachedStep ? parseInt(cachedStep, 10) : 0
  })

  const [data, setData] = useState(() => {
    const cachedData = localStorage.getItem("trustai_wizard_data")
    return cachedData ? JSON.parse(cachedData) : {
      goal: "",
      beneficiary: "",
      story: "",
      amountUsage: "",
      trustElements: "",
      tone: "EMOTIONAL"
    }
  })

  // Sync with localStorage
  React.useEffect(() => {
    localStorage.setItem("trustai_wizard_data", JSON.stringify(data))
    localStorage.setItem("trustai_wizard_step", step.toString())
  }, [data, step])

  const handleChange = (field, value) => {
    setData(prev => ({ ...prev, [field]: value }))
  }

  const totalSteps = 5

  const handleNext = () => {
    setStep(prev => Math.min(prev + 1, totalSteps - 1))
  }

  const handleBack = () => {
    setStep(prev => Math.max(prev - 1, 0))
  }

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <div style={{ marginTop: '10px' }}>
            <h3 style={{ marginBottom: '12px', fontSize: '1.1rem', color: '#1a1a1a' }}>
              {formatMessage({ id: "trustai::studio::step1::title", defaultMessage: "First, who is this campaign for?" })}
            </h3>
            <TextInput
              newDesign
              label={formatMessage({ id: "trustai::studio::beneficiary::label", defaultMessage: "Who is the beneficiary?" })}
              placeholder={formatMessage({ id: "trustai::studio::beneficiary::placeholder", defaultMessage: "e.g. Local high school students" })}
              value={data.beneficiary}
              onChange={e => handleChange("beneficiary", e.target.value)}
              maxLength={150}
            />
          </div>
        )
      case 1:
        return (
          <div style={{ marginTop: '10px' }}>
            <h3 style={{ marginBottom: '12px', fontSize: '1.1rem', color: '#1a1a1a' }}>
              {formatMessage({ id: "trustai::studio::step2::title", defaultMessage: "What is the main goal?" })}
            </h3>
            <TextInput
              newDesign
              label={formatMessage({ id: "trustai::studio::goal::label", defaultMessage: "What is the main goal of this campaign?" })}
              placeholder={formatMessage({ id: "trustai::studio::goal::placeholder", defaultMessage: "e.g. Raise funds for a new school library" })}
              value={data.goal}
              onChange={e => handleChange("goal", e.target.value)}
              maxLength={150}
            />
          </div>
        )
      case 2:
        return (
          <div style={{ marginTop: '10px' }}>
            <h3 style={{ marginBottom: '12px', fontSize: '1.1rem', color: '#1a1a1a' }}>
              {formatMessage({ id: "trustai::studio::step3::title", defaultMessage: "Tell us the story." })}
            </h3>
            <TextInput
              newDesign
              type="textarea"
              rows={4}
              label={formatMessage({ id: "trustai::studio::story::label", defaultMessage: "Tell us the story behind this campaign" })}
              placeholder={formatMessage({ id: "trustai::studio::story::placeholder", defaultMessage: "e.g. The current library was damaged in a storm..." })}
              value={data.story}
              onChange={e => handleChange("story", e.target.value)}
              maxLength={1500}
            />
          </div>
        )
      case 3:
        return (
          <div style={{ marginTop: '10px' }}>
            <h3 style={{ marginBottom: '12px', fontSize: '1.1rem', color: '#1a1a1a' }}>
              {formatMessage({ id: "trustai::studio::step4::title", defaultMessage: "Add some details to build trust." })}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <TextInput
                newDesign
                label={formatMessage({ id: "trustai::studio::amountUsage::label", defaultMessage: "How will the funds be used?" })}
                placeholder={formatMessage({ id: "trustai::studio::amountUsage::placeholder", defaultMessage: "e.g. Buying books, repairing roof..." })}
                value={data.amountUsage}
                onChange={e => handleChange("amountUsage", e.target.value)}
                maxLength={150}
              />
              <TextInput
                newDesign
                label={formatMessage({ id: "trustai::studio::trustElements::label", defaultMessage: "Trust elements (quotes, plans, etc.)" })}
                placeholder={formatMessage({ id: "trustai::studio::trustElements::placeholder", defaultMessage: "e.g. We have a quote from a contractor for $5000" })}
                value={data.trustElements}
                onChange={e => handleChange("trustElements", e.target.value)}
                maxLength={150}
              />
            </div>
          </div>
        )
      case 4:
        return (
          <div style={{ marginTop: '10px', textAlign: 'center' }}>
            <h3 style={{ marginBottom: '12px', fontSize: '1.1rem', color: '#1a1a1a' }}>
              {formatMessage({ id: "trustai::studio::step5::title", defaultMessage: "Choose the tone and generate!" })}
            </h3>

            <div style={{ maxWidth: '300px', margin: '0 auto', textAlign: 'left' }}>

              <SelectInput
                value={data.tone}
                onChange={(e) => handleChange("tone", e.target.value)}
                style={{ width: '100%' }}
              >
                <option value="EMOTIONAL">{formatMessage({ id: "trustai::tone::emotional", defaultMessage: "Emotional" })}</option>
                <option value="URGENT">{formatMessage({ id: "trustai::tone::urgent", defaultMessage: "Urgent" })}</option>
                <option value="PROFESSIONAL">{formatMessage({ id: "trustai::tone::professional", defaultMessage: "Professional" })}</option>
                <option value="FORMAL">{formatMessage({ id: "trustai::tone::formal", defaultMessage: "Formal" })}</option>
              </SelectInput>
            </div>
          </div>
        )
      default:
        return null
    }
  }

  return (
    <StudioContainer>
      <HeaderSection>
        <TitleRow>
          <Branding>
            <LogoSphere />
            <Title>{formatMessage({ id: "trustai::studio::title", defaultMessage: "TrustAI Studio" })}</Title>
          </Branding>

          <ControlsRow>
            <IconButton onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} size="lg" />
            </IconButton>
          </ControlsRow>
        </TitleRow>
        {/* Progress Dots */}
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '10px' }}>
          {[0, 1, 2, 3, 4].map(s => (
            <div
              key={s}
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: s === step ? '#00d2ff' : '#e0e0e0',
                transition: 'background-color 0.3s'
              }}
            />
          ))}
        </div>
      </HeaderSection>

      <FormContainer>
        {renderStep()}
      </FormContainer>

      <FooterActions style={{ justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          onClick={handleBack}
          disabled={step === 0}
          style={{ visibility: step === 0 ? 'hidden' : 'visible' }}
        >
          {formatMessage({ id: "trustai::studio::back", defaultMessage: "Back" })}
        </Button>

        {step < totalSteps - 1 ? (
          <Button onClick={handleNext}>
            {formatMessage({ id: "trustai::studio::next", defaultMessage: "Next" })}
          </Button>
        ) : (
          <GenerateButton
            onClick={() => onGenerate(data)}
            disabled={!data.goal || !data.story}
          >
            <FontAwesomeIcon icon={faSparkles} />
            {formatMessage({ id: "trustai::studio::generate", defaultMessage: "Generate Campaign" })}
          </GenerateButton>
        )}
      </FooterActions>

    </StudioContainer>
  )
}

export default TrustAIStudio
